import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { RemainingCreditsContext } from '../providers/RemainingCreditsProvider';
import supabase from '../supabase/supabase';
import { RatingModal } from '../components/RatingModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const REVIEW_MILESTONES_DAYS = [1, 2, 4, 7, 13, 24];
const ONE_HOUR_MS = 60 * 60 * 1000;

// AppSumo users get a day-0 milestone — but only once they've accumulated 1 h
const getMilestones = (isAppSumo: boolean) =>
  isAppSumo ? [0, ...REVIEW_MILESTONES_DAYS] : REVIEW_MILESTONES_DAYS;

// ─── localStorage key helpers ─────────────────────────────────────────────────

const getRatingKeys = (userId: string) => ({
  firstOpen: `wv_first_open_${userId}`,
  lastMilestone: `wv_rating_milestone_${userId}`,
  timeSpent: `wv_time_spent_${userId}`,
});

const getTimeSpentMs = (userId: string): number => {
  try {
    const stored = localStorage.getItem(`wv_time_spent_${userId}`);
    return stored ? parseInt(stored, 10) : 0;
  } catch {
    return 0;
  }
};

// ─── Milestone gate ───────────────────────────────────────────────────────────

/**
 * Returns true when the user should see the rating modal right now.
 *
 * Rules
 * ─────
 * • Day-0 (AppSumo only): fires once the user has ≥ 1 hour of accumulated
 *   app time.  This is the ONLY milestone with a time-spent requirement.
 * • All other milestones: fire as soon as the days-since-first-open threshold
 *   is crossed, regardless of time spent.
 *
 * Writes the triggered milestone to localStorage so it never fires twice.
 */
const checkShouldShow = (
  isAppSumo: boolean,
  userId: string,
  timeSpentMs: number
): boolean => {
  // Web modal is AppSumo-only
  if (!isAppSumo) return false;

  try {
    const keys = getRatingKeys(userId);
    const milestones = getMilestones(isAppSumo);

    // Record first-open on the very first call; never show on that same call
    // (time tracking has only just started).
    const firstOpenStr = localStorage.getItem(keys.firstOpen);
    if (!firstOpenStr) {
      localStorage.setItem(keys.firstOpen, Date.now().toString());
      return false;
    }

    const firstOpen = parseInt(firstOpenStr, 10);
    const daysSinceFirstOpen = (Date.now() - firstOpen) / (1000 * 60 * 60 * 24);

    const lastShownStr = localStorage.getItem(keys.lastMilestone);
    const lastShownMilestone = lastShownStr ? parseInt(lastShownStr, 10) : -1;

    for (const milestone of milestones) {
      if (daysSinceFirstOpen >= milestone && milestone > lastShownMilestone) {
        // Day-0 for AppSumo: gate on 1 hour of actual usage
        if (milestone === 0 && isAppSumo && timeSpentMs < ONE_HOUR_MS) {
          // Not enough time yet — keep looping in case a higher milestone is due
          continue;
        }
        localStorage.setItem(keys.lastMilestone, milestone.toString());
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
};

// ─── Context ──────────────────────────────────────────────────────────────────

interface RatingModalContextType {
  /** Imperatively open the rating modal (e.g. from a settings menu) */
  openRatingModal: () => void;
}

const RatingModalContext = createContext<RatingModalContextType>({
  openRatingModal: () => {},
});

export const useRatingModal = () => useContext(RatingModalContext);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const RatingModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAppSumo, setIsAppSumo] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Refs so the interval callbacks always see the latest values without
  // being recreated every render.
  const isOpenRef = useRef(false);
  const isAppSumoRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  const { limits, isLoading: creditsLoading } = useContext(RemainingCreditsContext);

  // Keep refs in sync
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { isAppSumoRef.current = isAppSumo; }, [isAppSumo]);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // Dev-only: expose window.__showRatingModal(true/false) for quick testing
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      (window as any).__showRatingModal = (asAppSumo = true) => {
        setIsAppSumo(asAppSumo);
        setIsOpen(true);
      };
      return () => { delete (window as any).__showRatingModal; };
    }
  }, []);

  // ── Auth: track user ID ───────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Time tracking ─────────────────────────────────────────────────────────
  // Accumulates visible/focused time in localStorage so it survives refreshes.
  // Saves every 30 s and on tab-hide / page-unload.
  useEffect(() => {
    if (!userId) return;

    const timeKey = `wv_time_spent_${userId}`;
    let sessionStart = Date.now();
    let pageVisible = !document.hidden;

    const flushTime = () => {
      if (!pageVisible) return;
      const elapsed = Date.now() - sessionStart;
      const prev = parseInt(localStorage.getItem(timeKey) ?? '0', 10);
      localStorage.setItem(timeKey, (prev + elapsed).toString());
      sessionStart = Date.now(); // reset so next flush doesn't double-count
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        flushTime();
        pageVisible = false;
      } else {
        sessionStart = Date.now();
        pageVisible = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', flushTime);

    // Flush every 30 s AND re-run the milestone check so the modal fires
    // within at most 30 s of the user hitting the 1-hour mark.
    const tick = setInterval(() => {
      flushTime();

      // Only re-check for AppSumo day-0 while the modal isn't already open
      if (isOpenRef.current) return;
      const uid = userIdRef.current;
      if (!uid || !isAppSumoRef.current) return;

      const timeSpentMs = getTimeSpentMs(uid);
      const should = checkShouldShow(true, uid, timeSpentMs);
      if (should) setIsOpen(true);
    }, 30_000);

    return () => {
      flushTime();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', flushTime);
      clearInterval(tick);
    };
  }, [userId]);

  // ── Initial milestone check (once credits + userId are ready) ─────────────
  useEffect(() => {
    if (!userId || creditsLoading) return;

    const isAS = limits?.isAppSumo ?? false;
    setIsAppSumo(isAS);

    // Small delay so the app finishes rendering first
    const timer = setTimeout(() => {
      const timeSpentMs = getTimeSpentMs(userId);
      const should = checkShouldShow(isAS, userId, timeSpentMs);
      if (should) setIsOpen(true);
    }, 800);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, creditsLoading]);
  // Intentionally excludes `limits` — we only check once per session when
  // credits first load, not on every periodic refresh.

  return (
    <RatingModalContext.Provider value={{ openRatingModal: () => setIsOpen(true) }}>
      {children}
      <RatingModal
        isOpen={isOpen}
        isAppSumo={isAppSumo}
        onDismiss={() => setIsOpen(false)}
      />
    </RatingModalContext.Provider>
  );
};
